<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconSparkles } from '@tabler/icons-vue'
import { Button, Input } from '@ui'
import type { AnalystAvailability } from '@/api/analyst'
import AnalystDisclosure from './AnalystDisclosure.vue'

/**
 * The prompt bar (P4 §2): suggestions for this screen in one row above the
 * question box, the box, and where the answer is worked out (§5.3). Where no AI may run on the workspace's
 * data it says so, and what still works, instead of offering a box that
 * cannot answer (§5.3, S0 §49b.4 rule 5).
 */
const props = defineProps<{
  placeholder: string
  /** Keys under `analyst.suggestions`; none inside the panel, where it is a follow-up. */
  suggestions: string[]
  /** Null while it is being read: nothing is offered until the Analyst is known to be able to answer. */
  availability: AnalystAvailability | null
  busy: boolean
}>()
const emit = defineEmits<{ ask: [question: string]; howItWorks: [] }>()
const { t } = useI18n()

const question = ref('')
const field = ref<{ input: HTMLInputElement | null } | null>(null)

const permitted = computed(() => props.availability?.status === 'PERMITTED')
const canSend = computed(() => permitted.value && !props.busy && question.value.trim().length > 0)

const send = (text: string): void => {
  if (!permitted.value || props.busy || !text.trim()) return
  emit('ask', text.trim())
  question.value = ''
}

defineExpose({ focus: () => field.value?.input?.focus() })
</script>

<template>
  <div v-if="availability" class="prompt-bar" data-test="analyst-bar">
    <template v-if="permitted">
      <!-- Above the box, in one row from its left edge, as chat products put them (Dao, 1 Oct 2026). -->
      <div v-if="suggestions.length > 0" class="prompt-bar__chips" role="group" :aria-label="t('analyst.bar.suggestions')">
        <button
          v-for="key in suggestions"
          :key="key"
          type="button"
          class="prompt-bar__chip"
          :disabled="busy"
          data-test="analyst-suggestion"
          @click="send(t(key))"
        >
          {{ t(key) }}
        </button>
      </div>
      <form class="prompt-bar__row" @submit.prevent="send(question)">
        <Input
          ref="field"
          v-model="question"
          size="lg"
          class="prompt-bar__input"
          :placeholder="placeholder"
          :aria-label="placeholder"
          maxlength="1000"
          aria-keyshortcuts="Control+K Meta+K"
          data-test="analyst-question"
        >
          <template #prefix><IconSparkles :size="18" class="prompt-bar__spark" aria-hidden="true" /></template>
        </Input>
        <!-- A button in a form submits it: Enter in the field and a click both send, once. -->
        <Button
          size="lg"
          :disabled="!canSend"
          :aria-disabled="!canSend"
          :loading="busy"
          data-test="analyst-send"
        >
          {{ t('analyst.bar.send') }}
        </Button>
      </form>

      <AnalystDisclosure @how-it-works="emit('howItWorks')" />
    </template>

    <!-- P4 §5.3: the constraint named, and what remains. Never silently routed elsewhere. -->
    <div v-else class="prompt-bar__unavailable" role="status" data-test="analyst-unavailable">
      <IconSparkles :size="18" class="prompt-bar__spark" aria-hidden="true" />
      <div>
        <p class="prompt-bar__unavailable-title">{{ t('analyst.unavailable.title') }}</p>
        <p class="prompt-bar__unavailable-body">
          {{ t(`inference.status.${availability.status}`) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prompt-bar {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-2xl);
  background: var(--surface-card);
  box-shadow: var(--shadow-card-float);
}

.prompt-bar__row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.prompt-bar__input {
  flex: 1 1 auto;
  min-width: 0;
}

.prompt-bar__spark {
  flex: none;
  color: var(--accent);
}

/* One row, scrolled sideways when it is longer than the box, never wrapped into a column. */
.prompt-bar__chips {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--spacing-xs);
  overflow-x: auto;
  scrollbar-width: none;
}

.prompt-bar__chip {
  flex: none;
  padding: var(--spacing-xxs) var(--spacing-md);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-full);
  background: var(--surface-card);
  font: inherit;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
  white-space: nowrap;
  cursor: pointer;
}

.prompt-bar__chip:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--fg-primary);
}

.prompt-bar__chip:disabled {
  cursor: default;
  color: var(--fg-disabled);
}

.prompt-bar__chip:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.prompt-bar__unavailable {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
}

.prompt-bar__unavailable-title {
  margin: 0;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-primary);
}

.prompt-bar__unavailable-body {
  margin: var(--spacing-xxs) 0 0;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}
</style>
